/* eslint-disable max-lines */
/* TODO: remove the above line and reduce the line count */

import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import cx from 'classnames';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ModalConfirmation from 'FRONTEND/components/Calendar/ModalConfirmation';
import { getCalendarMessages } from 'FRONTEND/components/Calendar/utils';
import {
  listRequest as listAppointmentsRequest,
} from 'FRONTEND/reducers/appointmentSlice';
import {
  listLocationSlots,
  listRequest as listSlotsRequest,
  selectAvailableSlotsFromDuration,
  listAllExpertsSlots,
} from 'FRONTEND/reducers/slotSlice';
import { selectAllNonCancelledAppointments } from 'FRONTEND/selectors/appointmentSelectors';
import { EXPERT, FormTypes } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';

dayjs.extend(isBetween);

const LOADING_SLOTS_REQUEST = [
  listLocationSlots.pending,
  listSlotsRequest,
];

const AppointmentSlotPicker = ({
  appointmentDuration,
  onAppointmentBooked,
}) => {
  const dispatch = useDispatch();
  const appointments = useSelector(selectAllNonCancelledAppointments);
  const availableSlots = useSelector(selectAvailableSlotsFromDuration);
  const expertId = useSelector((state) => state.booking.selectedExpertId);
  const additionalExpertIdList = useSelector((state) => state.booking.additionalSelectedExpertIdList);
  const selectedLocationId = useSelector((state) => state.booking.selectedLocationId);
  // Only used to know if slots need to be retrieved.
  const isLoadingAppointments = useSelector((state) => state.app.requestCounter[listAppointmentsRequest.toString()]);
  const getSlotsRequestStatus = useSelector((state) => LOADING_SLOTS_REQUEST.some((actionCreator) => state.app.requestCounter[actionCreator.toString()] > 0));

  const isSmall = useSelector((state) => state.app.isSmall);
  const locale = useSelector((state) => state.user.locale);
  const selectedType = useSelector((state) => state.booking.selectedType);
  const isLocationForm = useSelector((state) => state.booking.formType === FormTypes.LOCATION_FORM);
  const isPatientForm = useSelector((state) => state.booking.formType === FormTypes.PATIENT_FORM);
  const isBoothTeleconsultation = useSelector((state) => state.booking.isBooth);
  // All available slots sliced by duration.
  const [slotsPerDay, setSlotsPerDay] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [slotDetails, setSlotDetails] = useState({});
  const [currentWeekStart, setCurrentWeekStart] = useState(dayjs().startOf('week'));
  const [currentWeekEnd, setCurrentWeekEnd] = useState(dayjs().endOf('week'));
  const [sevenNextDays, setSevenNextDays] = useState([]);
  const [noSlotWeekMessage, setNoSlotWeekMessage] = useState(null);
  const [message, setMessage] = useState('');
  const nextWeekButtonRef = useRef(null);
  const [showAllSlots, setShowAllSlots] = useState(false);

  // Mobile view specific states
  const [availableDays, setAvailableDays] = useState([]);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);

  useEffect(() => {
    const newSevenNextDays = [];
    for (let day = currentWeekStart; day < currentWeekEnd; day = day.clone().add(1, 'd')) {
      newSevenNextDays.push(day);
    }
    setSevenNextDays(newSevenNextDays);
    setNoSlotWeekMessage(null);
    if (isPatientForm) {
      if (isBoothTeleconsultation) {
        dispatch(listLocationSlots({
          locationId: selectedLocationId,
          startDate: currentWeekStart.toISOString(),
          endDate: currentWeekEnd.toISOString(),
          appointmentType: selectedType,
          canAcceptPayment: true,
        }));
      } else {
        dispatch(listAllExpertsSlots({
          startDate: currentWeekStart.toISOString(),
          appointmentType: selectedType,
          canAcceptPayment: true,
        }));
      }
    } else if (isLocationForm) {
      dispatch(listLocationSlots({
        locationId: selectedLocationId,
        startDate: currentWeekStart.toISOString(),
        endDate: currentWeekEnd.toISOString(),
        appointmentType: selectedType,
      }));
    } else {
      dispatch(listSlotsRequest({
        expertIdList: [expertId, ...additionalExpertIdList],
        startDate: currentWeekStart.toISOString(),
        endDate: currentWeekEnd.toISOString(),
        appointmentType: selectedType,
      }));
    }
  }, [currentWeekStart]);

  useEffect(() => {
    setSlotsPerDay(availableSlots
      .filter((slot) => dayjs(slot.end).isAfter(currentWeekStart) && dayjs(slot.start).isBefore(currentWeekEnd))
      .reduce((acc, slot) => {
        const slotStartDay = dayjs(slot.start).hour(1);
        const dayIndex = slotStartDay.diff(currentWeekStart, 'days');
        acc[dayIndex].push(slot);
        return acc;
      }, { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] }),
    );

    const areSlotsCurrentWeek = availableSlots.some((slot) => dayjs(slot.end).isAfter(dayjs()) && dayjs(slot.start).isBefore(currentWeekEnd));
    const hasAnySlots = availableSlots.length > 0;
    const hasFutureSlots = availableSlots.some(slot =>
      dayjs(slot.start).isAfter(currentWeekStart.clone().add(7, 'days')),
    );

    if (!hasAnySlots) {
      setNoSlotWeekMessage(i18n._('No slots available.'));
    } else if (!areSlotsCurrentWeek && hasFutureSlots) {
      setNoSlotWeekMessage(i18n._('No slot available this week.'));
    } else {
      setNoSlotWeekMessage(null);
    }
  }, [availableSlots, currentWeekStart, currentWeekEnd, getSlotsRequestStatus]);

  // Process available slots for mobile view
  useEffect(() => {
    if (isSmall && availableSlots && availableSlots.length > 0) {
      // Get unique days from available slots
      const uniqueDays = availableSlots
        .filter(slot => dayjs(slot.start).isAfter(dayjs()))
        .reduce((days, slot) => {
          const dayKey = dayjs(slot.start).format('YYYY-MM-DD');
          if (!days[dayKey]) {
            days[dayKey] = {
              date: dayjs(slot.start).startOf('day'),
              slots: [],
            };
          }
          days[dayKey].slots.push(slot);
          return days;
        }, {});

      // Convert to array and sort chronologically
      const sortedDays = Object.values(uniqueDays).sort((a, b) =>
        a.date.valueOf() - b.date.valueOf(),
      );

      setAvailableDays(sortedDays);
      // Reset to first day when available days change
      setCurrentDayIndex(0);
    }
  }, [availableSlots, isSmall]);

  useEffect(() => {
    if (!isLoadingAppointments && isModalOpen) {
      isSlotBusy();
    }
  }, [isLoadingAppointments, isModalOpen]);

  useEffect(() => scrollToBottom(), [availableDays]);

  const scrollToBottom = () => {
    return nextWeekButtonRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  function isTimeIntersecting(start1, end1, start2, end2) {
    return start1 < end2 && end1 > start2;
  }

  const isSlotBusy = () => {
    // First we check if there are time conflicts between existing appointments, and selected slot
    const conflictingAppointments = appointments.filter(apt => isTimeIntersecting(apt.startDate, apt.endDate, slotDetails.startDate, slotDetails.endDate));

    // Then, we check if the id of the concerned expert is the same as one of the practicioner of those potentially conflicting appointments
    const expertIdFound = conflictingAppointments.some(apt =>
      apt.participant.some(actor => {
        return actor.actor.type === 'Practicioner' && actor.actor.id === expertId;
      }),
    );
    const info = expertIdFound ? (
      <>
        <div className='busySlot_warning'>
          <WarningIcon /> {i18n._('An appointment is already set for this time slot.')}.
        </div>
        {dayjs(slotDetails.startDate).locale(locale).format('LL [' + i18n._('at') + '] LT')}
      </>
    ) : (
      <>
        {dayjs(slotDetails.startDate).locale(locale).format('LL [' + i18n._('at') + '] LT')}
      </>
    );

    setMessage(info);
  };

  const onClose = (response) => {
    if (response) {
      onAppointmentBooked(slotDetails);
    }
    setIsModalOpen(false);
  };

  const getSlots = (shouldShowMore) => {
    if (availableSlots.length) {
      return Object.values(slotsPerDay).map((slot, index) => {
        return (
          <div key={index} className='appointmentsPicker-daySlots'>
            {getSlotsPerDay(slot, shouldShowMore)}
          </div>
        );
      });
    }
  };

  const capitalizeDate = (dateString) => {
    return dateString.charAt(0).toUpperCase() + dateString.slice(1);
  };

  const getSlotsPerDay = (slotsPerDay, shouldShowMore = false) => {
    const uniqueSlots = slotsPerDay.filter((slot, index, self) => index === self.findIndex((t) => t.start.isSame(slot.start)));
    const filteredSlots = uniqueSlots.filter((slot) => dayjs(slot.start).isAfter());

    // Only show 3 results per day unless "Show More" is pressed.
    const slotsToRender = shouldShowMore ? filteredSlots : filteredSlots.slice(0, 3);

    return slotsToRender.map((slot, index) => {
      const time = dayjs(slot.start).locale(locale).format('LT');
      return (
        <div key={index} className='appointmentsPicker-slot'>
          <Button
            className='appointmentsPicker-slotButton'
            variant='contained'
            sx={{
              height: '28px',
              minHeight: '28px',
              borderRadius: '11px',
              fontSize: '15px',
              textWrap: 'nowrap',
              fontWeight: '700',
              backgroundColor: '#6AABE9',
              '&:hover': {
                backgroundColor: '#1876D2',
              },
            }}
            key={slot.start}
            onClick={() => {
              setSlotDetails({
                startDate: slot.start.toISOString(),
                endDate: isLocationForm ? slot.end.toISOString() : slot.start.add(appointmentDuration, 'minutes').toISOString(),
                availabilityId: slot.availabilityId,
              });
              if (!slot.start.isBetween(currentWeekStart, currentWeekEnd)) {
                dispatch(listAppointmentsRequest({ actorId: expertId, actorType: EXPERT, startDate: dayjs(slot.start).startOf('day'), endDate: dayjs(slot.start).endOf('day') }));
              }
              setIsModalOpen(true);
            }}
          >
            {time}
          </Button>
        </div>
      );
    });
  };

  const containerClasses = cx('appointmentsPickerMobile', { appointmentsPicker_mobile: isSmall });

  // Get day information for mobile view
  const getCurrentDay = () => {
    if (!availableDays.length || currentDayIndex < 0 || currentDayIndex >= availableDays.length) {
      return { dayName: '', dayDate: '' };
    }

    const currentDay = availableDays[currentDayIndex].date;
    return {
      dayName: capitalizeDate(currentDay.locale(locale).format('dddd')),
      dayDate: currentDay.locale(locale).format('DD') + ' ' + capitalizeDate(currentDay.locale(locale).format('MMMM')),
    };
  };

  const getSlotsMobile = () => {
    const day = getCurrentDay();
    const hasNextDay = currentDayIndex < availableDays.length - 1;
    const hasPrevDay = currentDayIndex > 0;

    return (
      <div className={containerClasses}>
        {getSlotsRequestStatus && (
          <div className='appointmentsPicker-isLoading'>
            <span>{i18n._('Loading')}</span> <CircularProgress />
          </div>
        )}
        {availableDays.length > 0 ? (
          <>
            <Stack direction='row' width='100%'>
              <Button
                sx={{ minWidth: '5px', width: '5px' }}
                onClick={() => hasPrevDay && setCurrentDayIndex(currentDayIndex - 1)}
                disabled={!hasPrevDay}
              >
                {getCalendarMessages().previous}
              </Button>

              <Box sx={{ backgroundColor: '#E8E5E5', borderRadius: '11px', textAlign: 'center' }} width='100%'>
                <Typography fontWeight='700' fontSize={15}>{day.dayName}</Typography>
                <Typography color='#474646' fontSize={15}>{day.dayDate}</Typography>
              </Box>

              <Button
                sx={{ minWidth: '5px', width: '5px' }}
                onClick={() => hasNextDay && setCurrentDayIndex(currentDayIndex + 1)}
                disabled={!hasNextDay}
              >
                {getCalendarMessages().next}
              </Button>
            </Stack>

            <Stack
              sx={{
                overflowY: showAllSlots ? 'scroll' : 'none',
                paddingLeft: '3%',
                paddingRight: showAllSlots ? '15px' : '3%',
              }}
              maxHeight='300px'
              width='100%'
            >
              {availableDays[currentDayIndex]?.slots.length > 0 &&
                getSlotsPerDay(availableDays[currentDayIndex].slots, showAllSlots)}
            </Stack>

            {!showAllSlots && availableDays[currentDayIndex]?.slots.length > 3 &&
              <Button
                onClick={() => setShowAllSlots(true)}
                sx={{ fontWeight: 700, fontSize: '13px' }}
              >
                {i18n._('Show more slots')}
              </Button>}
          </>
        ) : i18n._('No available slots.')}
      </div>
    );
  };

  return (
    <>
      {!isSmall && (
        <>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {/* PREVIOUS WEEK BUTTON */}
            <Button
              sx={{
                position: 'absolute',
                minWidth: '5px',
                width: '5px',
                left: '-20px',
                top: '28px',
                transform: 'translateY(-50%)',
                zIndex: 2,
              }}
              onClick={() => {
                setCurrentWeekStart(currentWeekStart.clone().subtract(7, 'days'));
                setCurrentWeekEnd(currentWeekEnd.clone().subtract(7, 'days'));
              }}
              disabled={currentWeekEnd.clone().subtract(7, 'days').isBefore(dayjs())}
            >
              {getCalendarMessages().previous}
            </Button>

            {/* NEXT WEEK BUTTON */}
            <Button
              sx={{
                position: 'absolute',
                minWidth: '5px',
                width: '5px',
                right: '-20px',
                top: '28px',
                transform: 'translateY(-50%)',
                zIndex: 2,
              }}
              onClick={() => {
                setCurrentWeekStart(currentWeekStart.clone().add(7, 'days'));
                setCurrentWeekEnd(currentWeekEnd.clone().add(7, 'days'));
              }}
            >
              {getCalendarMessages().next}
            </Button>

            {/* SLOT PICKER GRID */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gridTemplateRows: 'auto auto',
                overflowY: showAllSlots ? 'scroll' : 'none',
                maxHeight: '300px',
                gap: '5px',
                position: 'relative',
                paddingRight: showAllSlots ? '8px' : '0px', // Add padding to create space for scrollbar
              }}
            >
              {/* FIRST ROW: 7 WEEK DAYS NAME + DATE */}
              {sevenNextDays.map((day, index) => (
                <div key={index} className='appointmentsPicker-dayTitle' style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <Typography fontWeight='700' fontSize={15}>{isSmall ? capitalizeDate(day.locale(locale).format('dd')) : capitalizeDate(day.locale(locale).format('dddd'))}</Typography>
                  <Typography color='#474646' fontSize={15}>{day.locale(locale).format('DD')} {capitalizeDate(day.locale(locale).format('MMMM'))}</Typography>
                </div>
              ))}

              {/* SECOND ROW: TIMESLOT COLUMNS & NO SLOT ERRORS/FALLBACKS */}
              {noSlotWeekMessage && !getSlotsRequestStatus ? (
                <Box
                  component='div'
                  className='appointmentsPicker-noSlot'
                  sx={{ gridColumn: 'span 7', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}
                >
                  {noSlotWeekMessage}
                  {availableSlots.length > 0 && (
                    <Button
                      variant='outlined'
                      onClick={() => {
                        setCurrentWeekStart(dayjs(availableSlots.find((slot) => dayjs(slot.start).isAfter(currentWeekEnd)).start).startOf('week'));
                        setCurrentWeekEnd(dayjs(availableSlots.find((slot) => dayjs(slot.start).isAfter(currentWeekEnd)).start).endOf('week'));
                      }}
                    >
                      {i18n._('Next slot on') + ' ' + dayjs(availableSlots.find((slot) => dayjs(slot.start).isAfter(currentWeekEnd))?.start).format('DD MMM YYYY')}
                      <NavigateNextIcon />
                    </Button>
                  )}
                </Box>
              ) : !getSlotsRequestStatus && getSlots(showAllSlots)}
            </Box>
          </Box>

          {/* SHOW MORE SLOTS BUTTON & LOADING STATE */}
          <Stack justifyContent='center' alignItems='center'>
            {getSlotsRequestStatus && (
              <div className='appointmentsPicker-isLoading'>
                <span>{i18n._('Loading')}</span><CircularProgress />
              </div>
            )}
            {(!isSmall &&
              !showAllSlots &&
              !getSlotsRequestStatus &&
              availableSlots.some(slot =>
                dayjs(slot.start).isAfter(currentWeekStart) &&
                dayjs(slot.start).isBefore(currentWeekEnd),
              )
            ) && (
              <Button onClick={() => setShowAllSlots(true)} sx={{ fontWeight: 700, fontSize: '13px' }}>
                {i18n._('Show more slots')}
              </Button>
            )}
          </Stack>
        </>
      )}
      <ModalConfirmation
        open={isModalOpen}
        onClose={onClose}
        message={message}
      />
      {isSmall && getSlotsMobile()}
    </>
  );
};

export default AppointmentSlotPicker;
